import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion, onDocumentLoaded } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} [resetButton] - The reset button element (optional for custom search).
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
class PredictiveSearchComponent extends Component {
  requiredRefs = ['searchInput', 'predictiveSearchResults'];

  #controller = new AbortController();

  /**
   * @type {AbortController | null}
   */
  #activeFetch = null;

  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }

  connectedCallback() {
    super.connectedCallback();

    const { dialog } = this;
    const { signal } = this.#controller;

    if (this.refs.searchInput.value.length > 0) {
      this.#showResetButton();
    }

    document.addEventListener('geoip:country', this.#applyVisibleLimits, { signal });
    onDocumentLoaded(() => {
      if (signal.aborted) return;
      this.#applyVisibleLimits();
    });

    if (dialog) {
      document.addEventListener('keydown', this.#handleKeyboardShortcut, { signal });
      dialog.addEventListener(DialogCloseEvent.eventName, this.#handleDialogClose, { signal });

      this.addEventListener('click', this.#handleModalClick, { signal });
    }

    // For custom search widget (non-dialog), setup overlay click handler
    if (!dialog) {
      onDocumentLoaded(() => {
        // Add click handler to search overlay
        const searchOverlay = document.querySelector('.search-overlay');
        if (searchOverlay) {
          searchOverlay.addEventListener('click', () => {
            this.resetSearch(false);
            this.#hideResultsContainer();
            this.classList.remove('has-search-value');
          }, { signal });
        }

        // Add click handler to close search button
        const closeSearchButton = this.querySelector('#close-search-button');
        if (closeSearchButton) {
          closeSearchButton.addEventListener('click', () => {
            this.resetSearch(false);
            this.#hideResultsContainer();
            this.classList.remove('has-search-value');
            // Hide mobile search container
            const mobileSearch = document.querySelector('.tor-header-mobile__search');
            if (mobileSearch instanceof HTMLElement) {
              mobileSearch.style.display = 'none';
            }
          }, { signal });
        }
      });
    }
  }

  /**
   * Handles clicks within the predictive search modal to maintain focus on the input
   * @param {MouseEvent} event - The mouse event
   */
  #handleModalClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);
    const isInteractiveElement =
      target instanceof HTMLButtonElement ||
      target instanceof HTMLAnchorElement ||
      target instanceof HTMLInputElement ||
      target.closest('button') ||
      target.closest('a') ||
      target.closest('input');

    if (!isInteractiveElement && this.refs.searchInput) {
      this.refs.searchInput.focus();
    }
  };

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#controller.abort();
  }

  /**
   * Handles the CMD+K key combination.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyboardShortcut = (event) => {
    if (event.metaKey && event.key === 'k') {
      this.dialog?.toggleDialog();
    }
  };

  /**
   * Handles the dialog close event.
   */
  #handleDialogClose = () => {
    this.#resetSearch();
  };

  get #allResultsItems() {
    const containers = Array.from(
      this.querySelectorAll(
        '.predictive-search-results__wrapper-queries, ' +
          '.predictive-search-results__wrapper-products, ' +
          '.predictive-search-results__list'
      )
    );

    const allItems = containers
      .flatMap((container) => {
        if (container.classList.contains('predictive-search-results__wrapper-products')) {
          return Array.from(container.querySelectorAll('.predictive-search-results__card'));
        }
        return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
      })
      .filter((item) => item instanceof HTMLElement && item.offsetParent !== null);

    return /** @type {HTMLElement[]} */ (allItems);
  }

  /**
   * Track whether the last interaction was keyboard-based
   * @type {boolean}
   */
  #isKeyboardNavigation = false;

  get #currentIndex() {
    return this.#allResultsItems?.findIndex((item) => item.getAttribute('aria-selected') === 'true') ?? -1;
  }

  set #currentIndex(index) {
    if (!this.#allResultsItems?.length) return;

    this.#allResultsItems.forEach((item) => {
      item.classList.remove('keyboard-focus');
    });

    for (const [itemIndex, item] of this.#allResultsItems.entries()) {
      if (itemIndex === index) {
        item.setAttribute('aria-selected', 'true');

        if (this.#isKeyboardNavigation) {
          item.classList.add('keyboard-focus');
        }
        item.scrollIntoView({ behavior: prefersReducedMotion() ? 'instant' : 'smooth', block: 'nearest' });
      } else {
        item.removeAttribute('aria-selected');
      }
    }
    this.refs.searchInput.focus();
  }

  get #currentItem() {
    return this.#allResultsItems?.[this.#currentIndex];
  }

  /**
   * Navigate through the predictive search results using arrow keys or close them with the Escape key.
   * @param {KeyboardEvent} event - The keyboard event.
   */
  onSearchKeyDown = (event) => {
    if (event.key === 'Escape') {
      this.#resetSearch();
      return;
    }

    if (!this.#allResultsItems?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      return;
    }

    const currentIndex = this.#currentIndex;
    const totalItems = this.#allResultsItems.length;

    switch (event.key) {
      case 'ArrowDown':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        break;

      case 'Tab':
        if (event.shiftKey) {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        } else {
          this.#isKeyboardNavigation = true;
          event.preventDefault();
          this.#currentIndex = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
        }
        break;

      case 'ArrowUp':
        this.#isKeyboardNavigation = true;
        event.preventDefault();
        this.#currentIndex = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
        break;

      case 'Enter': {
        const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
        if (
          singleResultContainer instanceof HTMLElement &&
          singleResultContainer.dataset.singleResultUrl &&
          getComputedStyle(singleResultContainer).display !== 'none'
        ) {
          event.preventDefault();
          window.location.href = singleResultContainer.dataset.singleResultUrl;
          return;
        }

        if (this.#currentIndex >= 0) {
          event.preventDefault();
          this.#currentItem?.querySelector('a')?.click();
        } else {
          const searchUrl = new URL(Theme.routes.search_url, location.origin);
          searchUrl.searchParams.set('q', this.refs.searchInput.value);
          window.location.href = searchUrl.toString();
        }
        break;
      }
    }
  };

  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();

    RecentlyViewed.clearProducts();

    const { recentlyViewedItems, recentlyViewedTitle, recentlyViewedWrapper } = this.refs;

    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];

    if (allRecentlyViewedElements.length === 0) {
      return;
    }

    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');

      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }

  /**
   * Reset the search state.
   * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
   */
  resetSearch = debounce((keepFocus = true) => {
    if (keepFocus) {
      this.refs.searchInput.focus();
    }
    this.#resetSearch();
  }, 100);

  /**
   * Debounce the search handler to fetch and display search results based on the input value.
   * Reset the current selection index and close results if the search term is empty.
   */
  search = debounce((event) => {
    // If the input is not a text input (like using the Escape key), don't search
    if (!event.inputType) return;

    const searchTerm = this.refs.searchInput.value.trim();
    this.#currentIndex = -1;

    if (!searchTerm.length) {
      this.classList.remove('has-search-value');
      this.#hideResetButton();
      this.#hideResultsContainer();
      return;
    }

    this.classList.add('has-search-value');
    this.#showResetButton();
    this.#showResultsContainer();
    this.#getSearchResults(searchTerm);
  }, 200);

  /**
   * Resets scroll positions for search results containers
   */
  #resetScrollPositions() {
    requestAnimationFrame(() => {
      const resultsInner = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner');
      if (resultsInner instanceof HTMLElement) {
        resultsInner.scrollTop = 0;
      }

      const formContent = this.querySelector('.predictive-search-form__content');
      if (formContent instanceof HTMLElement) {
        formContent.scrollTop = 0;
      }
    });
  }

  /**
   * Initialize Swiper
  */
  #initializeSwiper() {
    const swiperContainer = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__list-swiper');
    const queriesWrapper = this.refs.predictiveSearchResults.querySelector('.predictive-search-results__wrapper-queries');
    if (swiperContainer && queriesWrapper && typeof Swiper !== 'undefined') {
      if (swiperContainer.swiper) {
        swiperContainer.swiper.destroy(true, true);
      }
      new Swiper(swiperContainer, {
        loop: false,
        spaceBetween: 10,
        slidesPerView: "auto",
        watchSlidesProgress: true,
        scrollbar: {
          el: '.predictive-search-results__list-scrollbar',
          draggable: true,
        },
      });
    }
  }

  /**
   * Fetch search results using the section renderer and update the results container.
   * @param {string} searchTerm - The term to search for
   */
  async #getSearchResults(searchTerm) {
    if (!this.dataset.sectionId) return;

    const url = new URL(Theme.routes.predictive_search_url, location.origin);
    url.searchParams.set('q', searchTerm);
    url.searchParams.set('resources[limit_scope]', 'each');

    // Build resources types and limits based on data attributes
    const resourceTypes = ['product']; // Always include products
    
    // Include queries if enabled or attribute not set (default to true)
    const showQueries = this.dataset.showQueries !== 'false';
    if (showQueries) {
      resourceTypes.unshift('query'); // Add query at the beginning
    }
    
    const collectionLimit = parseInt(this.dataset.showCollection) || 0;
    if (collectionLimit > 0) {
      resourceTypes.push('collection');
    }
    
    const postsLimit = parseInt(this.dataset.showPosts) || 0;
    if (postsLimit > 0) {
      resourceTypes.push('article');
    }
    
    const pagesLimit = parseInt(this.dataset.showPages) || 0;
    if (pagesLimit > 0) {
      resourceTypes.push('page');
    }

    url.searchParams.set('resources[type]', resourceTypes.join(','));
    url.searchParams.set('resources[options][fields]', 'title,product_type,variants.title,vendor,variants.sku');
    
    // Set individual limits for each resource type
    if (collectionLimit > 0) {
      url.searchParams.set('resources[options][collection][limit]', collectionLimit.toString());
    }
    if (postsLimit > 0) {
      url.searchParams.set('resources[options][article][limit]', postsLimit.toString());
    }
    if (pagesLimit > 0) {
      url.searchParams.set('resources[options][page][limit]', pagesLimit.toString());
    }

    const { predictiveSearchResults } = this.refs;

    const abortController = this.#createAbortController();

    sectionRenderer
      .getSectionHTML(this.dataset.sectionId, false, url)
      .then((resultsMarkup) => {
        if (!resultsMarkup) return;

        if (abortController.signal.aborted) return;

        morph(predictiveSearchResults, resultsMarkup);

        this.#applyVisibleLimits();
        this.#resetScrollPositions();
        this.#initializeSwiper();
      })
      .catch((error) => {
        if (abortController.signal.aborted) return;
        throw error;
      });
  }

  /**
   * Fetch the markup for the recently viewed products.
   * @returns {Promise<string | null>} The markup for the recently viewed products.
   */
  async #getRecentlyViewedProductsMarkup() {
    if (!this.dataset.sectionId) return null;

    const viewedProducts = RecentlyViewed.getProducts();
    if (viewedProducts.length === 0) return null;

    const url = new URL(Theme.routes.search_url, location.origin);
    url.searchParams.set('q', viewedProducts.map(/** @param {string} id */ (id) => `id:${id}`).join(' OR '));
    url.searchParams.set('resources[type]', 'product');

    return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
  }

  #hideResetButton() {
    const { resetButton } = this.refs;

    if (resetButton) {
      resetButton.hidden = true;
    }
  }

  #showResetButton() {
    const { resetButton } = this.refs;

    if (resetButton) {
      resetButton.hidden = false;
    }
  }

  #showResultsContainer() {
    const wrapper = this.querySelector('.predictive-search-form__content-wrapper');
    if (wrapper instanceof HTMLElement) {
      wrapper.style.display = '';
    }
    
    const results = this.refs.predictiveSearchResults?.querySelector('#predictive-search-results');
    if (results instanceof HTMLElement) {
      results.setAttribute('aria-expanded', 'true');
    }

    // Set header-component to active state when search is opened
    const headerComponent = document.getElementById('header-component');
    if (headerComponent) {
      const currentState = headerComponent.getAttribute('data-sticky-state');
      if (currentState !== 'active') {
        // Store previous state if not already active
        this._previousStickyState = currentState;
        headerComponent.setAttribute('data-sticky-state', 'active');
      }
    }
  }

  #hideResultsContainer() {
    const wrapper = this.querySelector('.predictive-search-form__content-wrapper');
    if (wrapper instanceof HTMLElement) {
      wrapper.style.display = 'none';
    }
    
    const results = this.refs.predictiveSearchResults?.querySelector('#predictive-search-results');
    if (results instanceof HTMLElement) {
      results.setAttribute('aria-expanded', 'false');
    }

    // Restore previous sticky state when search is closed
    const headerComponent = document.getElementById('header-component');
    if (headerComponent && this._previousStickyState !== undefined) {
      if (this._previousStickyState === null) {
        headerComponent.removeAttribute('data-sticky-state');
      } else {
        headerComponent.setAttribute('data-sticky-state', this._previousStickyState);
      }
      this._previousStickyState = undefined;
    }
  }

  #createAbortController() {
    const abortController = new AbortController();
    if (this.#activeFetch) {
      this.#activeFetch.abort();
    }
    this.#activeFetch = abortController;
    return abortController;
  }

  #resetSearch = async () => {
    const { predictiveSearchResults, searchInput } = this.refs;
    const emptySectionId = 'predictive-search-empty';

    this.#currentIndex = -1;
    searchInput.value = '';
    this.classList.remove('has-search-value');
    this.#hideResetButton();
    this.#hideResultsContainer();

    const abortController = this.#createAbortController();
    
    // Clear the results container
    if (predictiveSearchResults) {
      const resultsDiv = predictiveSearchResults.querySelector('#predictive-search-results');
      if (resultsDiv) {
        resultsDiv.innerHTML = '';
      }
    }

    // Only load empty state for dialog (popup) mode, not for custom search widget
    if (!this.dialog) return;

    const url = new URL(window.location.href);
    url.searchParams.delete('page');

    const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
    const parsedEmptySectionMarkup = new DOMParser()
      .parseFromString(emptySectionMarkup, 'text/html')
      .querySelector('.predictive-search-empty-section');

    if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

    /** This needs to be awaited and not .then so the DOM is already morphed
     * when #closeResults is called and therefore the height is animated */
    const viewedProducts = RecentlyViewed.getProducts();

    if (viewedProducts.length > 0) {
      const recentlyViewedMarkup = await this.#getRecentlyViewedProductsMarkup();
      if (!recentlyViewedMarkup) return;

      const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
      const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
      if (!recentlyViewedProductsHtml) return;

      for (const child of recentlyViewedProductsHtml.children) {
        if (child instanceof HTMLElement) {
          child.setAttribute('ref', 'recentlyViewedWrapper');
        }
      }

      const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
      if (!collectionElement) return;
      collectionElement.prepend(...recentlyViewedProductsHtml.children);
    }

    if (abortController.signal.aborted) return;

    morph(predictiveSearchResults, parsedEmptySectionMarkup);
    this.#applyVisibleLimits();
    this.#resetScrollPositions();
  };

  /**
   * Caps each product list to its data-visible-limit, counting only cards not hidden by geo filtering.
   */
  #applyVisibleLimits = () => {
    const { predictiveSearchResults } = this.refs;
    if (!predictiveSearchResults) return;

    predictiveSearchResults.querySelectorAll('[data-visible-limit]').forEach((list) => {
      const limit = parseInt(list.getAttribute('data-visible-limit') || '', 10);
      if (!Number.isFinite(limit) || limit < 0) return;

      const cards = Array.from(list.querySelectorAll('.predictive-search-results__card'));
      cards.forEach((card) => card.classList.remove('hidden'));

      const displays = cards.map((card) => getComputedStyle(card).display);

      let visibleCount = 0;
      cards.forEach((card, index) => {
        if (displays[index] === 'none') return;
        visibleCount += 1;
        if (visibleCount > limit) {
          card.classList.add('hidden');
        }
      });
    });

    this.#announceVisibleResults();
  };

  /**
   * Keeps the live region in sync with what is actually on screen after geo and limit filtering.
   */
  #announceVisibleResults = () => {
    const { predictiveSearchResults } = this.refs;
    const announcement = predictiveSearchResults?.querySelector('[data-results-count-text]');
    if (!(announcement instanceof HTMLElement) || !announcement.textContent?.trim()) return;

    const visibleCount = Array.from(
      predictiveSearchResults.querySelectorAll(
        '.predictive-search-results__card, .predictive-search-results__card--query, .predictive-search-results__other-item'
      )
    ).filter((element) => getComputedStyle(element).display !== 'none').length;

    const next =
      visibleCount === 0
        ? announcement.dataset.noResultsText || ''
        : (announcement.dataset.resultsCountText || '').replace('__COUNT__', String(visibleCount));

    if (next && announcement.textContent.trim() !== next) {
      announcement.textContent = next;
    }
  };
}

if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
